/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, type ComponentProps } from 'react';
import Video, { type VideoProps } from 'components/Video/Video';
import Picture from 'components/Picture/Picture';
import Thumbs from './Thumbs/Thumbs';
import './VideoGallery.scss';

const DEFAULT_VIDEOS: VideoItem[] = [];

interface VideoItem {
  video: VideoProps;
  thumb: ComponentProps<typeof Picture>;
}

interface VideoGalleryProps extends Omit<
  ComponentProps<typeof Thumbs>,
  'list' | 'onSelect'
> {
  videos?: VideoItem[];
}

/**
 * This is the VideoGallery component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function VideoGallery({
  videos = DEFAULT_VIDEOS,
  ...props
}: VideoGalleryProps) {
  const [active, setActive] = useState(0);
  const [autoplay, setAutoplay] = useState(false);
  if (!videos[active]) return null;

  const thumbs = videos.map(({ thumb, video }, index) => ({
    index,
    ...thumb,
    title: video.text,
    duration: video.duration,
  }));

  const played = thumbs.slice(0, active + 1);
  const queue = thumbs.slice(active + 1);

  function renderThumbs() {
    if (videos.length < 2) {
      return null;
    }

    return (
      <Thumbs
        list={[...queue, ...played]}
        onSelect={index => {
          setActive(index as number);
          setAutoplay(true);
        }}
        {...props}
      />
    );
  }

  return (
    <div className="block video-gallery" appear="true">
      <Video
        key={active}
        autoPlay={autoplay}
        onEnded={() => {
          setActive(index => {
            if (index + 1 < videos.length) {
              return index + 1;
            }

            return 0;
          });
          setAutoplay(true);
        }}
        {...videos[active].video}
      />
      {renderThumbs()}
    </div>
  );
}

export default VideoGallery;
